// The project's members in the app (W9c; core `members`, PROTOCOL.md §10.13): read from the live project document,
// once per version of it, and the small pieces the pages share — a member's CSS colour, who made a comment or a
// checkpoint, and whether the project is shared at all (no `members`: every page shows what it always did).
import type * as A from '@automerge/automerge';
import { ENTITY_HUES, entityColorString, rgbToCSS, type DailyWords, type OpenedProject, type Project } from '@writing-suite/core';
import {
  logOfDevices, readMembers, UNKNOWN_COLOR, whoMadeObject, wordsByPerson, writerColor, writerName, type Members, type PersonWords, type WrittenBy,
} from '@writing-suite/core/members';
import type { LiveProject } from './project.ts';

const cache = new WeakMap<object, Members>();

/** The members of the open project as its document records them now; null when it has none (never shared). */
export function membersOf(live: Pick<LiveProject, 'doc'> | null | undefined): Members | null {
  const doc = live?.doc('project');
  if (!doc) return null;
  let m = cache.get(doc);
  if (!m) {
    m = readMembers(doc);
    cache.set(doc, m);
  }
  return m.list.length ? m : null;
}

/** "r g b" as CSS (the grey of an unknown device when unreadable). */
export const colorCSS = (rgb: string | null | undefined) => rgbToCSS(rgb) ?? rgbToCSS(UNKNOWN_COLOR)!;

/** Who made the object at `path` in a document (a comment's, a suggestion's or a checkpoint's map), as a name and a
 *  CSS colour; null when the project isn't shared or the object isn't there. */
export function madeBy(members: Members | null, doc: A.Doc<unknown> | null | undefined, path: string[]): { name: string; color: string; known: boolean } | null {
  if (!members || !doc) return null;
  const w: WrittenBy | null = whoMadeObject(members, doc, path);
  return w ? { name: writerName(w), color: colorCSS(writerColor(w)), known: w.kind === 'member' } : null;
}

// MARK: Revision marks by author (a per-device choice, as showing the marks is)

const BY_AUTHOR = 'ws:revisionMarksByAuthor';
const byAuthorListeners = new Set<() => void>();
let byAuthorVersion = 0;

/** Revision mode colours the added words by who wrote them (shared projects), else by revision. */
export function revisionsByAuthor(): boolean {
  try {
    return localStorage.getItem(BY_AUTHOR) === 'true';
  } catch {
    return false;
  }
}

export function setRevisionsByAuthor(on: boolean) {
  try {
    localStorage.setItem(BY_AUTHOR, String(on));
  } catch {
    // this page only
  }
  byAuthorVersion++;
  for (const fn of byAuthorListeners) fn();
}

export function subscribeByAuthor(fn: () => void): () => void {
  byAuthorListeners.add(fn);
  return () => byAuthorListeners.delete(fn);
}

export const byAuthorSnapshot = () => byAuthorVersion;

/** A member's colour as an entity hue index (0…11) for the editor's marks; members whose colour isn't one of the hues
 *  take one by their order. */
export function hueIndex(color: string, order: number): number {
  const i = ENTITY_HUES.findIndex((h) => entityColorString(h) === color);
  return i >= 0 ? i : order % ENTITY_HUES.length;
}

// MARK: Statistics by person

/** The counted fragments' word logs (as Statistics counts them). */
function countedLogs(project: OpenedProject): Record<string, number>[] {
  return project.counted(project.targets().countAllText).flatMap((id) => {
    const log = project.fragmentRecord(id)?.wordLog;
    return log ? [log] : [];
  });
}

/** Written and cut by each person in a shared project (its word logs' devices put down to members), else null. */
export function wordsOfPeople(project: Project, live: Pick<LiveProject, 'doc'> | null | undefined): PersonWords[] | null {
  const members = membersOf(live);
  return members ? wordsByPerson(members, countedLogs(project as OpenedProject)) : null;
}

/** The words this account wrote, by day (the Library home's "this week" stays your own in a shared project); the
 *  project's own figures when it isn't shared. */
export function ownDaily(project: Project, live: Pick<LiveProject, 'doc' | 'memberId'> | null | undefined): DailyWords[] {
  const members = membersOf(live);
  const me = live?.memberId ? members?.byId.get(live.memberId) : undefined;
  if (!members || !me) return project.daily();
  const devices = new Set(me.devices);
  const logs = countedLogs(project as OpenedProject).map((log) => logOfDevices(log, devices));
  return wordsByPerson({ ...members, list: [me], byDevice: new Map([...devices].map((d) => [d, me])) }, logs)[0]?.daily ?? [];
}
